import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

const DEFAULT_SUPABASE_URL = 'https://oaebnpzsfppuvnsekusc.supabase.co';
const DEFAULT_SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9hZWJucHpzZnBwdXZuc2VrdXNjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE5OTU5MDUsImV4cCI6MjA1NzU3MTkwNX0.O97AzzE5AM-ho6s2C8x7jyIMipI6GFDe5uM9L2F2TVI';

export const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || DEFAULT_SUPABASE_URL).trim();
export const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || DEFAULT_SUPABASE_ANON_KEY).trim();

if (!supabaseUrl.startsWith('http')) {
  console.error('Invalid VITE_SUPABASE_URL — must be a valid URL.');
}

if (supabaseAnonKey.length < 20) {
  console.error('Invalid VITE_SUPABASE_ANON_KEY.');
}

export const supabase = createClient<Database>(
  supabaseUrl, 
  supabaseAnonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: localStorage
    },
    global: {
      headers: {
        'x-application-name': 'vholic-appliances'
      }
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
);

// Enhanced connection test with more specific error handling
export async function testConnection(): Promise<void> {
  try {
    // First check if we can reach the Supabase URL
    const urlTest = await fetch(supabaseUrl);
    if (!urlTest.ok) {
      throw new Error(`Unable to reach Supabase server at ${supabaseUrl}`);
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.warn('No active session, but connection successful');
    }

    const { data, error } = await supabase
      .from('products')
      .select('id')
      .limit(1)
      .maybeSingle();

    if (error) {
      if (error.code === 'PGRST301') {
        throw new Error('Database connection failed. Please verify your Supabase credentials.');
      } else if (error.code === 'PGRST204') {
        // No data but connection successful
        return;
      }
      throw error;
    }

    // Connection successful
    return;
  } catch (error) {
    console.error('Supabase connection error:', error);
    
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('Network error: Unable to connect to Supabase. Please check your internet connection and firewall settings.');
    }
    
    if (error.message?.includes('No active session')) {
      throw new Error('Authentication required. Please log in.');
    }
    
    if (error.message?.includes('JWT')) {
      throw new Error('Authentication failed. Please verify your Supabase API key.');
    }

    throw new Error(`Database connection error: ${error.message || 'Unknown error'}`);
  }
}

// Retry mechanism for database operations with exponential backoff
export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  initialDelay = 1000
): Promise<T> {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      console.warn(`Operation failed (attempt ${attempt}/${maxRetries}):`, error);
      
      if (attempt < maxRetries) {
        // Exponential backoff with jitter
        const delay = initialDelay * Math.pow(2, attempt - 1) * (0.9 + Math.random() * 0.2);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
}

// Refresh session if needed
export async function refreshSession(): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (session && session.expires_at) {
    const expiresAt = new Date(session.expires_at * 1000);
    const now = new Date();
    
    // Refresh if token expires in less than 5 minutes
    if ((expiresAt.getTime() - now.getTime()) < 5 * 60 * 1000) {
      await supabase.auth.refreshSession();
    }
  }
}

// Subscribe to product changes
export function subscribeToProducts(callback: (payload: any) => void) {
  return supabase
    .channel('products_changes')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'products' },
      callback
    )
    .subscribe();
}

// Update product category
export async function updateProductCategory(
  productId: string,
  category: 'New' | 'Used' | 'Like New'
): Promise<{ success: boolean; error?: string }> {
  return retryOperation(async () => {
    try {
      const { error } = await supabase
        .from('products')
        .update({ 
          category,
          updated_at: new Date().toISOString()
        })
        .eq('id', productId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error updating product category:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to update product category' 
      };
    }
  });
}

// Get public URL for storage items
export function getPublicUrl(path: string): string {
  const { data: { publicUrl } } = supabase.storage
    .from('product-images')
    .getPublicUrl(path);
  return publicUrl;
}